import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getAnnouncementsHistory } from '../../../api/announcements.api';
import { queryKeys } from '../../../api/queryKeys';
import type { ActiveAnnouncement } from '../../../types/api/announcements.types';
import { isKnownAnnouncementType } from '../helpers/announcementContent';

const PAGE_SIZE = 20;
const FIRST_PAGE = 1;

const fetchPage = async (page: number) => {
  const { data } = await getAnnouncementsHistory({ page, limit: PAGE_SIZE });
  const items = data?.data ?? [];

  // No total in the response: a short page is the last one.
  return {
    items,
    nextPage: items.length === PAGE_SIZE ? page + 1 : undefined,
  };
};

/**
 * Every What's New this app + platform has had, newest first.
 *
 * Read-only on purpose: the backend leaves seen state alone here, and so does
 * this hook - opening the history never calls `mark-as-seen`, so an
 * announcement still due as a popup stays due.
 *
 * Kept fresh for five minutes, so going back and forth between Settings and
 * the list does not refetch: entries change at the pace of releases.
 */
export const useAnnouncementsHistory = () => {
  const query = useInfiniteQuery({
    queryKey: queryKeys.announcementsHistory.all,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    getNextPageParam: (last) => last.nextPage,
    initialPageParam: FIRST_PAGE,
    staleTime: 5 * 60 * 1000,
  });

  // Types this build cannot draw are skipped, same as the popup does.
  const items = useMemo<ActiveAnnouncement[]>(
    () =>
      (query.data?.pages.flatMap((page) => page.items) ?? []).filter((item) =>
        isKnownAnnouncementType(item.type)
      ),
    [query.data]
  );

  return {
    items,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetchNextPageError: query.isFetchNextPageError,
    fetchNextPage: query.fetchNextPage,
  };
};
